/**
 * SprintWaterfall — Gantt-style timeline for sprint epics/issues
 *
 * Shared between In-Flight (live) and Completed (static) pages.
 * When `staticMode` is true: no NOW line, no tick interval, no stripe animation.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { SprintEpic } from '../../api/sprint';

/* ── Constants ────────────────────────────────────────────── */

const LANE_GAP = 1;
const EPIC_HEADER_H = 28;
const ISSUE_BAR_H = 24;
const LABEL_W = 180;
const RULER_H = 26;

const STATUS_BAR: Record<string, { bg: string; stripe: boolean }> = {
  pending:   { bg: '#e5e5e4', stripe: false },
  building:  { bg: '#D97F6F', stripe: true },
  active:    { bg: '#D97F6F', stripe: true },
  completed: { bg: '#52A560', stripe: false },
  failed:    { bg: '#C0392B', stripe: false },
  skipped:   { bg: '#a8a29e', stripe: false },
};

/* ── Types ────────────────────────────────────────────────── */

export interface WaterfallIssue {
  id: string;
  label: string;
  epicId: string;
  status: 'pending' | 'building' | 'completed' | 'failed';
  startOffset: number; // ms offset from sprint start
  duration: number;    // ms
}

/** Phase event from sprite activity telemetry */
export interface PhaseEvent {
  issueId: string;
  phase: string;
  timestamp: string;
}

const PHASE_COLORS: Record<string, string> = {
  spawning: '#a3a3a3', cloning: '#a3a3a3', installing: '#d6d3d1',
  coding: '#D97F6F', executing: '#D97F6F',
  quality_gate: '#3b82f6', pushing: '#52A560',
  retrying: '#C0392B', cleanup: '#d6d3d1',
};

export interface SprintWaterfallProps {
  epics: SprintEpic[];
  issues: WaterfallIssue[];
  sprintStartTime: string;
  /** End time override for static mode (e.g. sprint.completedAt) */
  endTime?: string;
  selectedIssueId?: string;
  onSelectIssue: (issue: WaterfallIssue | null) => void;
  /** When true: no NOW line, no live tick, no stripe animation */
  staticMode?: boolean;
  /** Map of epicId → display title (from work service) */
  epicTitleMap?: Map<string, string>;
  /** Phase events for rendering sub-bars within issue Gantt bars */
  phaseEvents?: PhaseEvent[];
}

/* ── Component ────────────────────────────────────────────── */

export function SprintWaterfall({
  epics,
  issues,
  sprintStartTime,
  endTime,
  selectedIssueId,
  onSelectIssue,
  staticMode = false,
  epicTitleMap,
  phaseEvents,
}: SprintWaterfallProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(600);
  const [liveNow, setLiveNow] = useState(Date.now());

  // Tick interval only for live mode
  useEffect(() => {
    if (staticMode) return;
    const id = setInterval(() => setLiveNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [staticMode]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerW(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sprintStart = new Date(sprintStartTime).getTime();
  const staticEnd = endTime ? new Date(endTime).getTime() : undefined;

  // Build phase segments per issue from phase events
  const phaseSegments = useMemo(() => {
    if (!phaseEvents || phaseEvents.length === 0) return new Map<string, Array<{ phase: string; startMs: number; endMs: number }>>();
    const map = new Map<string, Array<{ phase: string; ts: number }>>();
    for (const pe of phaseEvents) {
      if (!pe.issueId || !pe.phase) continue;
      if (!map.has(pe.issueId)) map.set(pe.issueId, []);
      map.get(pe.issueId)!.push({ phase: pe.phase, ts: new Date(pe.timestamp).getTime() });
    }
    const result = new Map<string, Array<{ phase: string; startMs: number; endMs: number }>>();
    for (const [issueId, phases] of map) {
      phases.sort((a, b) => a.ts - b.ts);
      const segments: Array<{ phase: string; startMs: number; endMs: number }> = [];
      for (let i = 0; i < phases.length; i++) {
        const endTs = i < phases.length - 1 ? phases[i + 1].ts : (staticEnd || liveNow);
        segments.push({ phase: phases[i].phase, startMs: phases[i].ts - sprintStart, endMs: endTs - sprintStart });
      }
      result.set(issueId, segments);
    }
    return result;
  }, [phaseEvents, sprintStart, staticEnd, liveNow]);

  // Compute time range
  const { rangeEnd, timelineW } = useMemo(() => {
    if (issues.length === 0) return { rangeEnd: sprintStart + 3600_000, timelineW: containerW - LABEL_W };

    const maxIssueEnd = Math.max(...issues.map(i => sprintStart + i.startOffset + i.duration));
    let maxEnd: number;
    if (staticMode && staticEnd) {
      maxEnd = Math.max(maxIssueEnd, staticEnd);
    } else {
      maxEnd = Math.max(maxIssueEnd, liveNow);
    }
    const end = maxEnd + (maxEnd - sprintStart) * 0.05;
    return { rangeEnd: end, timelineW: Math.max(containerW - LABEL_W, 300) };
  }, [issues, sprintStart, containerW, liveNow, staticMode, staticEnd]);

  const rangeDuration = rangeEnd - sprintStart;
  const pxPerMs = timelineW / (rangeDuration || 1);
  const toX = useCallback((ms: number) => ms * pxPerMs, [pxPerMs]);

  // Build rows: epic headers + issue bars
  const rows = useMemo(() => {
    const result: Array<{ type: 'epic'; epic: SprintEpic } | { type: 'issue'; issue: WaterfallIssue }> = [];
    for (const epic of epics) {
      result.push({ type: 'epic', epic });
      const epicIssues = issues.filter(i => i.epicId === epic.epicId);
      for (const issue of epicIssues) {
        result.push({ type: 'issue', issue });
      }
    }
    return result;
  }, [epics, issues]);

  // Ticks
  const ticks = useMemo(() => {
    const targets = [60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000, 60 * 60_000];
    let interval = targets[targets.length - 1];
    for (const t of targets) {
      if (rangeDuration / t <= 15 && rangeDuration / t >= 3) { interval = t; break; }
    }
    const result: { x: number; label: string }[] = [];
    for (let t = interval; t < rangeDuration; t += interval) {
      const mins = Math.floor(t / 60_000);
      result.push({
        x: toX(t),
        label: mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h${mins % 60 > 0 ? `${mins % 60}m` : ''}`,
      });
    }
    return result;
  }, [rangeDuration, toX]);

  // Now line (live mode only)
  const nowOffset = liveNow - sprintStart;
  const nowX = toX(nowOffset);

  const totalH = RULER_H + rows.reduce((h, r) =>
    h + (r.type === 'epic' ? EPIC_HEADER_H + LANE_GAP : ISSUE_BAR_H + LANE_GAP), 0,
  ) + 8;

  return (
    <div ref={containerRef} className="bg-white border border-neutral-200 rounded-lg shadow-warm-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-neutral-100">
        <div className="flex items-center gap-2">
          <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Sprint Waterfall</p>
          <span className="text-[10px] text-neutral-300 font-normal">
            {epics.length} epic{epics.length !== 1 ? 's' : ''}, {issues.length} issue{issues.length !== 1 ? 's' : ''}
          </span>
        </div>
        {/* Status summary row */}
        {issues.length > 0 && (() => {
          const completed = issues.filter(i => i.status === 'completed').length;
          const failed = issues.filter(i => i.status === 'failed').length;
          const building = issues.filter(i => i.status === 'building').length;
          const pending = issues.filter(i => i.status === 'pending').length;
          return (
            <div className="mt-1.5 text-[10px] text-neutral-500 flex items-center gap-3 flex-wrap">
              {completed > 0 && <span className="text-accent-600 font-medium">{'\u2713'} {completed} completed</span>}
              {failed > 0 && <span className="text-red-500 font-medium">{'\u2717'} {failed} failed</span>}
              {building > 0 && <span className="text-coral-500 font-medium">{'\u25CF'} {building} building</span>}
              {pending > 0 && <span className="text-neutral-400">{'\u25CB'} {pending} pending</span>}
              <span className="text-neutral-300">|</span>
              <span>Total: {issues.length} stories across {epics.length} epics</span>
            </div>
          );
        })()}
      </div>

      <div className="overflow-x-auto overflow-y-auto max-h-[500px]" style={{ scrollbarGutter: 'stable' }}>
        <div className="relative" style={{ width: LABEL_W + timelineW, height: totalH, minWidth: '100%' }}>

          {/* Ruler */}
          <div
            className="sticky top-0 z-10 flex"
            style={{ height: RULER_H, background: 'linear-gradient(to bottom, #fafaf9, #f5f5f4)' }}
          >
            <div className="shrink-0 border-r border-neutral-200 flex items-center px-3" style={{ width: LABEL_W }}>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Epic / Issue</span>
            </div>
            <div className="relative flex-1 border-b border-neutral-200">
              {/* 0 mark */}
              <div className="absolute top-0 bottom-0 flex flex-col items-center justify-end pb-1" style={{ left: 0 }}>
                <div className="w-px h-2 bg-neutral-300" />
                <span className="text-[9px] font-mono text-neutral-400 mt-0.5 select-none">0</span>
              </div>
              {ticks.map((tick, i) => (
                <div key={i} className="absolute top-0 bottom-0 flex flex-col items-center justify-end pb-1" style={{ left: tick.x }}>
                  <div className="w-px h-2 bg-neutral-300" />
                  <span className="text-[9px] font-mono text-neutral-400 mt-0.5 select-none">{tick.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {(() => {
            let y = RULER_H;
            return rows.map((row) => {
              if (row.type === 'epic') {
                const el = (
                  <div
                    key={`epic-${row.epic.epicId}`}
                    className="absolute left-0 flex items-center bg-neutral-50/80 border-b border-neutral-100"
                    style={{ top: y, width: LABEL_W + timelineW, height: EPIC_HEADER_H }}
                  >
                    <div className="px-3 flex items-center gap-2" style={{ width: LABEL_W }}>
                      <span className={`w-2 h-2 rounded-full shrink-0 ${
                        row.epic.status === 'completed' ? 'bg-accent-500' :
                        row.epic.status === 'building' ? 'bg-coral-500 animate-pulse' :
                        row.epic.status === 'failed' ? 'bg-red-500' : 'bg-neutral-300'
                      }`} />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-600 truncate">
                        {epicTitleMap?.get(row.epic.epicId) || `Epic ${row.epic.order}`}
                      </span>
                      {/* Epic progress bar */}
                      {(() => {
                        const epicIssues = issues.filter(i => i.epicId === row.epic.epicId);
                        const done = epicIssues.filter(i => i.status === 'completed').length;
                        const total = epicIssues.length;
                        if (total === 0) return null;
                        const pct = (done / total) * 100;
                        return (
                          <span className="flex items-center gap-1 shrink-0">
                            <span className="w-12 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                              <span className="block h-full bg-accent-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </span>
                            <span className="text-[9px] font-mono text-neutral-400">{done}/{total}</span>
                          </span>
                        );
                      })()}
                    </div>
                    {/* Tick gridlines */}
                    <div className="relative flex-1 h-full">
                      {ticks.map((tick, ti) => (
                        <div key={ti} className="absolute top-0 bottom-0 w-px bg-neutral-100" style={{ left: tick.x }} />
                      ))}
                    </div>
                  </div>
                );
                y += EPIC_HEADER_H + LANE_GAP;
                return el;
              }

              const issue = row.issue;
              const barCfg = STATUS_BAR[issue.status] || STATUS_BAR.pending;
              const barLeft = toX(issue.startOffset);
              const barWidth = Math.max(4, toX(issue.duration));
              const isSelected = selectedIssueId === issue.id;
              const showStripe = barCfg.stripe && !staticMode;

              const el = (
                <div
                  key={issue.id}
                  className="absolute left-0 flex"
                  style={{ top: y, width: LABEL_W + timelineW, height: ISSUE_BAR_H }}
                >
                  {/* Label */}
                  <div
                    className={`shrink-0 border-r border-neutral-100 flex items-center px-3 pl-8 cursor-pointer hover:bg-neutral-50 transition-colors ${
                      isSelected ? 'bg-coral-50/50' : ''
                    }`}
                    style={{ width: LABEL_W }}
                    onClick={() => onSelectIssue(isSelected ? null : issue)}
                  >
                    <span className={`text-[10px] truncate ${isSelected ? 'font-semibold text-coral-700' : 'text-neutral-600'}`}>
                      {issue.label}
                    </span>
                  </div>

                  {/* Bar area */}
                  <div className="relative flex-1" style={{ height: ISSUE_BAR_H }}>
                    {ticks.map((tick, ti) => (
                      <div key={ti} className="absolute top-0 bottom-0 w-px bg-neutral-50" style={{ left: tick.x }} />
                    ))}
                    <div
                      className={`absolute top-1 rounded-sm cursor-pointer transition-all hover:opacity-90 overflow-hidden ${
                        isSelected ? 'ring-2 ring-coral-400 ring-offset-1' : ''
                      }`}
                      style={{
                        left: barLeft,
                        width: barWidth,
                        height: ISSUE_BAR_H - 8,
                        backgroundColor: barCfg.bg,
                        ...(showStripe && !phaseSegments.has(issue.id) ? {
                          backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(255,255,255,0.15) 4px, rgba(255,255,255,0.15) 8px)',
                          backgroundSize: '11.3px 11.3px',
                          animation: 'waterfall-stripe 0.8s linear infinite',
                        } : {}),
                      }}
                      onClick={() => onSelectIssue(isSelected ? null : issue)}
                    >
                      {/* Phase sub-bars when phase data is available */}
                      {phaseSegments.has(issue.id) && (() => {
                        const segs = phaseSegments.get(issue.id)!;
                        const issueStartMs = issue.startOffset;
                        const issueDur = issue.duration || 1;
                        return segs.map((seg, si) => {
                          const segLeft = Math.max(0, (seg.startMs - issueStartMs) / issueDur) * 100;
                          const segWidth = Math.min(100 - segLeft, ((seg.endMs - seg.startMs) / issueDur) * 100);
                          if (segWidth <= 0) return null;
                          const color = PHASE_COLORS[seg.phase] || barCfg.bg;
                          const isLastActive = si === segs.length - 1 && !staticMode && (issue.status === 'building');
                          return (
                            <div
                              key={si}
                              className="absolute top-0 bottom-0"
                              style={{
                                left: `${segLeft}%`,
                                width: `${segWidth}%`,
                                backgroundColor: color,
                                ...(isLastActive ? {
                                  backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(255,255,255,0.15) 4px, rgba(255,255,255,0.15) 8px)',
                                  backgroundSize: '11.3px 11.3px',
                                  animation: 'waterfall-stripe 0.8s linear infinite',
                                } : {}),
                              }}
                              title={`${seg.phase} · ${Math.round((seg.endMs - seg.startMs) / 60_000)}m`}
                            />
                          );
                        });
                      })()}
                      {/* Status icon at end of bar */}
                      {issue.status === 'completed' && (
                        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-white/90 font-bold select-none z-[1]">{'\u2713'}</span>
                      )}
                      {issue.status === 'failed' && (
                        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-white/90 font-bold select-none z-[1]">{'\u2717'}</span>
                      )}
                      {barWidth > 60 && (
                        <span className={`absolute top-1/2 -translate-y-1/2 text-[8px] font-mono font-medium text-white/80 select-none z-[1] ${
                          issue.status === 'completed' || issue.status === 'failed' ? 'right-5' : 'right-2'
                        }`}>
                          {Math.round(issue.duration / 60_000)}m
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
              y += ISSUE_BAR_H + LANE_GAP;
              return el;
            });
          })()}

          {/* NOW line — live mode only */}
          {!staticMode && nowX > 0 && nowX < timelineW && (
            <div
              className="absolute z-10 pointer-events-none"
              style={{ left: LABEL_W + nowX, top: 0, bottom: 0, width: 0, borderLeft: '1.5px dashed #C0392B' }}
            >
              <div className="absolute -left-[14px] text-[8px] font-mono font-bold text-red-600 bg-red-50 px-1 rounded-b" style={{ top: 0 }}>
                NOW
              </div>
            </div>
          )}
        </div>
      </div>

      {!staticMode && (
        <style>{`
          @keyframes waterfall-stripe {
            from { background-position: 0 0; }
            to { background-position: 11.3px 0; }
          }
        `}</style>
      )}
    </div>
  );
}

export default SprintWaterfall;
